"use client";

import AdDetailModel from "@/models/AdDetailModel";
import { Box, styled, Typography } from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { AD_DETAILS_2_COLUMN_MAX_WIDTH, AD_DETAILS_BORDER_RADIUS } from "@/app/layout/LayoutConstants";
import { useState } from "react";
import Image from "next/image";
import useEmblaCarousel from "embla-carousel-react";
import dynamic from "next/dynamic";

const ThumbnailSpacingTwoColumn = 4;

const GalleryItem = styled(Box)({
  cursor: "pointer",
  position: "relative",
  paddingTop: "calc(100% * (150 / 200))",
  img: {
    position: "absolute",
    top: "0",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    overflow: "hidden",
  },
});

const LIGHTBOX_CLOSED_INDEX = -1;

export default function PreviewGallery({ ad }: { ad: AdDetailModel }) {
  const [activeIndex, setActiveIndex] = useState(LIGHTBOX_CLOSED_INDEX);

  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
  });

  if (ad.gallery.imageCount === 0) {
    return (
      <Box
        sx={(theme) => ({
          gridColumn: "2",
          height: theme.spacing(6),
          background: "white",
        })}
      />
    );
  }

  const LightboxGallery =
    activeIndex > LIGHTBOX_CLOSED_INDEX
      ? dynamic(() => import("./LightboxGallery"), {
          ssr: false,
        })
      : null;

  return (
    <>
      <Box
        className="embla"
        ref={emblaRef}
        sx={(theme) => ({
          overflow: "hidden",
          gridColumn: "2",
          borderTopLeftRadius: theme.spacing(AD_DETAILS_BORDER_RADIUS),
          borderTopRightRadius: theme.spacing(AD_DETAILS_BORDER_RADIUS),
          [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
            gridColumn: "1",
            borderRadius: 0
          },
        })}
      >
        <Box
          className="embla__container"
          sx={{
            display: "flex",
            height: "100%",
          }}
        >
          {ad.gallery.items.map((image, index) => (
            <GalleryItem
              key={index}
              className="embla__slide"
              onClick={() => setActiveIndex(index)}
              sx={{
                flex: "0 0 100%",
                minWidth: 0,
              }}
            >
              <Image src={image.preview} fill sizes="1000px" alt="" unoptimized />
            </GalleryItem>
          ))}
        </Box>
      </Box>

      <Box
        sx={(theme) => ({
          flex: 1,
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gridTemplateRows: "repeat(3, 1fr)",
          [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
            display: "none",
          },
        })}
      >
        {[...Array(6).keys()].map((index) => {
          const image = ad.gallery.items[index + 1];
          return (
            <Box
              key={index}
              sx={{
                paddingLeft: ThumbnailSpacingTwoColumn,
                paddingBottom: ThumbnailSpacingTwoColumn,                
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  backgroundColor: "grey.200",
                  borderRadius: AD_DETAILS_BORDER_RADIUS,
                  cursor: "pointer",
                  img: {
                    borderRadius: AD_DETAILS_BORDER_RADIUS,
                  },
                }}
              >
                {/* AJB: use only one, add alts */}
                {image && (
                  <GalleryItem onClick={() => setActiveIndex(index + 1)}>
                    <Image
                      src={image.thumbnail}
                      fill
                      alt=""
                      sizes="200px" // AJB: optimize size
                      unoptimized
                    />
                  </GalleryItem>
                )}
                {index === 5 && ad.gallery.imageCount > 7 && (
                  <Typography
                    sx={(theme) => ({
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundColor: "rgba(0,0,0,0.6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      borderRadius: AD_DETAILS_BORDER_RADIUS,
                      gap: 1,
                      padding: 2,
                      textAlign: "center",
                      [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
                        display: "none",
                      },
                    })}
                    onClick={() => setActiveIndex(index + 1)}
                  >
                    View all photos ({ad.gallery.imageCount}) <OpenInFullIcon />
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {LightboxGallery && (
        <LightboxGallery
          ad={ad}
          isOpen={true}
          defaultActiveIndex={activeIndex}
          onClose={() => setActiveIndex(LIGHTBOX_CLOSED_INDEX)}
        />
      )}
    </>
  );
}
